const { formatCurrency } = require('../constants.js');
const { RoomForm } = require('./RoomForm.js');

const CategoryRoomCalculator = {
	components: {
		RoomForm
	},

	setup() {
		const { ref, computed, onMounted, watch } = Vue;

		// Category-specific state
		const products = ref(Array.from(document.querySelectorAll('.product')));
		const currentRooms = ref([]);
		const currentPrimaryIndex = ref(1);

		const calculatorVisible = computed(() => {
			let length = document.querySelectorAll('.product .fk-room-price').length;
			let app = document.querySelector('#category-calculator-app');
			const forceVisible = app?.hasAttribute('data-always-visible');

			if (length == 0 && !forceVisible) {
				app?.classList.add('hidden');
			} else {
				app?.classList.remove('hidden');
			}

			return length > 0 || forceVisible;
		});

		const observeProductChanges = () => {
			const targetNode = document.querySelector('.products');

			if (targetNode) {
				const observer = new MutationObserver((mutations) => {
					mutations.forEach((mutation) => {
						if (mutation.type === 'childList') {
							products.value = Array.from(targetNode.children);
						}
					});
				});

				observer.observe(targetNode, { childList: true });
			}
		};

		const getPrices = (productType, projectSize, packSize, pricePerUnit2, regularPriceUnit2, productPrice, productRegularPrice, bulkTiers) => {
			let total;
			let regularTotal;
			let packsNeeded = Math.ceil(projectSize / packSize);

			if (productType === 'pack') {
				total = packsNeeded * productPrice;
				regularTotal = packsNeeded * productRegularPrice;
			} else if (productType === 'roll') {
				total = projectSize * pricePerUnit2;
				regularTotal = projectSize * regularPriceUnit2;
			}

			if (bulkTiers) {
				const highestDiscount = bulkTiers.reduce((maxDiscount, tier) => {
					const bulkDiscountQty = Number(tier.bulk_discount_qty);
					const bulkDiscountPercent = Number(tier['bulk_discount_%']);
					let quantity = productType == 'pack' ? packsNeeded : projectSize;

					return quantity >= bulkDiscountQty && bulkDiscountPercent > maxDiscount
						? bulkDiscountPercent
						: maxDiscount;
				}, 0);

				if (highestDiscount > 0) {
					total -= total * (highestDiscount / 100);
				}
			}

			return {
				totalPrice: total,
				regularTotalPrice: regularTotal,
			};
		};

		const updatePrices = () => {
			if (currentRooms.value.length === 0 ||
				isNaN(currentPrimaryIndex.value) ||
				currentPrimaryIndex.value > currentRooms.value.length) {
				products.value.forEach((product) => {
					const room = product.querySelector('.fk-room-price');
					if (room) {
						room.innerHTML = '';
						room.classList.add('hidden');
					}
				});
				return;
			}

			const primaryRoom = currentRooms.value[currentPrimaryIndex.value - 1];

			products.value.forEach((product) => {
				const room = product.querySelector('.fk-room-price');
				if (!room) return;

				room.classList.remove('hidden');
				const defaultPrice = product.querySelector('.fk-default-price');

				const projectSize = primaryRoom.projectSize;
				const productType = room.dataset.productType;
				const productRegularPrice = room.dataset.productRegularPrice;
				const bulkTiers = JSON.parse(room.dataset.bulkTiers.replace(/\\/g, '').slice(1, -1));
				let productPrice = room.dataset.productPrice;
				let packSize = room.dataset.packSize;
				let pricePerUnit2 = parseFloat(room.dataset.pricePerM2);
				let regularPriceUnit2 = parseFloat(room.dataset.regularPricePerM2);

				let { totalPrice, regularTotalPrice } = getPrices(
					productType,
					projectSize,
					packSize,
					pricePerUnit2,
					regularPriceUnit2,
					productPrice,
					productRegularPrice,
					bulkTiers
				);

				const unit = ' per m\u00B2';

				defaultPrice.innerHTML = `
					<div class="text-lg font-semibold leading-none text-brand-500">
						${formatCurrency(pricePerUnit2) + unit}
					</div>`;

				if (totalPrice != regularTotalPrice) {
					defaultPrice.innerHTML += `
						<div class="ml-3 text-sm leading-none line-through text-shades-500">
							${formatCurrency(regularPriceUnit2) + unit}
						</div>`;
				}

				room.innerHTML = `
					<div class="p-4 rounded-md bg-shades-50 text-shades-500 roomSizeProduct">
						<div class="text-sm font-normal">
							${primaryRoom.roomName} (${projectSize} m\u00B2)
						</div>
						<div class="flex items-center">
							<div class="text-lg font-semibold text-brand-500">
								${formatCurrency(totalPrice)}
							</div>
							<div class="ml-3 text-sm leading-none line-through">
								${regularTotalPrice != totalPrice ? formatCurrency(regularTotalPrice) : ''}
							</div>
						</div>
					</div>
				`;
			});
		};

		const onRoomsChanged = ({ rooms, primaryRoomIndex }) => {
			currentRooms.value = rooms;
			currentPrimaryIndex.value = primaryRoomIndex;
			updatePrices();
		};

		watch(products, () => {
			updatePrices();
		});

		onMounted(() => {
			observeProductChanges();
		});

		return {
			calculatorVisible,
			onRoomsChanged
		};
	},

	template: `
		<div
			class="flex justify-center items-center w-full md:justify-start"
			v-if="calculatorVisible"
		>
			<RoomForm
				variant="category"
				@rooms-changed="onRoomsChanged"
				@room-saved="onRoomsChanged"
				@room-deleted="onRoomsChanged"
			/>
		</div>
	`
};

module.exports = { CategoryRoomCalculator };
